<template>
  <v-sheet class="w-100 fill-height border rounded" elevation="1">
    <v-row v-if="isConverting" no-gutters class="fill-height">
      <v-col cols="12" class="d-flex justify-center align-center flex-column">
        <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
        <p class="text-body-1 font-weight-medium mt-2">
          {{ $t('messages.convertingYourCreative') }}
        </p>
      </v-col>
    </v-row>
    <v-row v-else-if="isConvertingError || emptyProducts" no-gutters class="fill-height">
      <v-col cols="12" class="d-flex justify-center align-center flex-column">
        <v-icon size="50">mdi-alert-circle-outline</v-icon>
        <p class="text-body-1 font-weight-medium mt-2" v-if="isConvertingError">
          {{ $t('messages.errorConvertingCreative') }}
        </p>
        <p class="text-body-1 font-weight-medium mt-2" v-else>
          {{ $t('messages.noProductsPresent') }}
        </p>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script lang="ts" setup>
import ProductsNotPresentException from '@/errors/ProductsNotPresentException'
import { autoFixCreative } from '@/helpers/creativeHelper'
import { useAutofixStore } from '@/stores/autofix'
import type { FooterConfigProvider } from '@/types/footerConfig'
import { inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

const isConverting = ref(false)
const isConvertingError = ref(false)
const emptyProducts = ref(false)
const autofixError = ref(false)
const disableRetry = ref(false)
const router = useRouter()
const route = useRoute()
const { footerConfig, setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!
const handleBack = ref(false)
const autofixStore = useAutofixStore()
const convertCreative = async () => {
  isConverting.value = true
  disableRetry.value = true
  handleBack.value = true
  try {
    autofixStore.isAutofixHappening = true
    await autoFixCreative(abortController.signal)
    isConverting.value = false
    autofixError.value = false
    if (autofixStore.isAutofixHappening) {
      router.replace({ name: 'Editor' })
      autofixStore.isAutofixHappening = false
    }
  } catch (error: any) {
    if (error instanceof ProductsNotPresentException) {
      isConverting.value = false
      emptyProducts.value = true
      handleBack.value = true
      return
    } else if (error.name === 'AbortError') {
      handleBack.value = false
      disableRetry.value = false
      isConverting.value = false
      isConvertingError.value = true
    }
    handleBack.value = false
    disableRetry.value = false
    autofixError.value = true
    isConverting.value = false
    isConvertingError.value = true
  }
}

watch(handleBack, (newValue) => {
  footerConfig.disableBack = newValue
})

onMounted(async () => {
  setFooterConfig({
    showGenerate: false,
    showBack: true,
    showRetry: false,
    onRetry: convertCreative,
    disableBack: handleBack.value,
  })
  window.onbeforeunload = () => {
    if (route.name === 'Convert') return ''
  }
  await convertCreative()
})

onUnmounted(() => {
  setFooterConfig({
    showRetry: false,
    disableBack: false,
    onNext: () => {},
  })
  window.onbeforeunload = null
})

const abortController = new AbortController()

onBeforeRouteLeave((to, from, next) => {
  if (from.name === 'Convert' && to.name === 'Mode') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      abortController.abort()
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})

watch(autofixError, (newVal) => {
  footerConfig.showRetry = newVal
})
</script>

<style scoped></style>
